<script lang="ts" setup>
import { computed, Ref, ref, toRef } from 'vue';
import Chip from 'primevue/chip';
import { TreeNode } from 'primevue/treenode';
import { useToast } from 'primevue/usetoast';
import { OrganizationInterface, OrganizationsInterface, OrganizationTableInterface } from '@/types';
import AddOrganizationModal from '@/components/AddOrganizationModal/AddOrganizationModal.vue';
import OrganizationConnections from './OrganizationConnections.vue';
import { useAuthStore, useClinicianOrganizationStore } from '@/store';
import { EMIT, PERMISSIONS } from '@/enum';
import { can } from '@/composables';

interface OrganizationPropsInterface {
  tableData?: OrganizationInterface[];
  clinicianId?: number | undefined;
  flatOrganizations: TreeNode[];
  loading?: boolean;
}

const props = defineProps<OrganizationPropsInterface>();
const emit = defineEmits<{ (e: EMIT.UPDATE): void }>();
const authStore = useAuthStore();
const organizationClinicianStore = useClinicianOrganizationStore();
const organizations: Ref<OrganizationInterface[] | undefined> = toRef(props, 'tableData');
const flatOrganizations = toRef(props, 'flatOrganizations');
const organizationCount: Ref<number> = computed(() => (organizations.value ? organizations.value.length : 0));
const organizationModalOpen = ref(false);
const toast = useToast();
const organizationItems: Ref<OrganizationsInterface[]> = ref([]);
const addEditOrgsButtonText = computed<string>(() =>
  organizations.value?.length ? 'Edit Organizations' : 'Add Organizations',
);

const createMenuModel = (item: OrganizationTableInterface) => {
  if (authStore.currentInstanceIsMasterTenant || !can([PERMISSIONS.CLINICIAN_DETACH_ORGANIZATIONS])) {
    return [];
  }

  const last = item[item.length - 1];
  const id = last.id;
  const name = last.name;
  return [
    {
      label: 'Remove Organization',
      command: () => detachOrganization([id], name),
    },
  ];
};

const attachOrganization: (ids: number[]) => Promise<void> = async (ids) => {
  await organizationClinicianStore.attachOrganizations({
    organizations: ids,
    clinicianId: props.clinicianId as number,
  });
  emit(EMIT.UPDATE);
  toast.add({
    severity: 'success',
    summary: 'Added',
    detail: 'Organization(s) have been added',
    life: 3000,
  });
};

const detachOrganization: (id: number[], organizationName?: string) => Promise<void> = async (
  ids,
  organizationName,
) => {
  await organizationClinicianStore.detachOrganizations({
    organizations: ids,
    clinicianId: props.clinicianId as number,
  });

  const detail = organizationName
    ? `Organization ${organizationName} has been removed from this Service Provider`
    : `Organization(s) have been removed from this Service Provider`;

  emit(EMIT.UPDATE);
  toast.add({
    severity: 'success',
    summary: 'Removed',
    detail,
    life: 3000,
  });
};

const openAddOrganizations = async () => {
  if (
    can([PERMISSIONS.CLINICIAN_ATTACH_ORGANIZATIONS]) &&
    can([PERMISSIONS.CLINICIAN_ORGANIZATIONS_AVAILABLE_TO_ADD])
  ) {
    organizationItems.value = await organizationClinicianStore.getAddOrganizationsModalData(
      props.clinicianId as number,
    );
    organizationModalOpen.value = true;
  } else {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'You do not have permission to add organizations',
      life: 3000,
    });
  }
};

const getUsersCurrentOrganizations = computed(() => {
  const selectedItems: Record<number, Record<string, boolean>> = {};
  const addOrgToObject = (organization: TreeNode) => {
    if (Array.isArray(organization)) organization = organization[0];
    selectedItems[Number(organization.id)] = { checked: true, partialChecked: false };
    if (organization.children?.length) organization.children.forEach(addOrgToObject);
  };
  flatOrganizations.value?.forEach(addOrgToObject);

  return selectedItems;
});

const onCancelAdd = () => (organizationModalOpen.value = false);

const onSaveAdd = (organizations: number[]) => {
  const previousOrganizationIds = Object.keys(getUsersCurrentOrganizations.value).map((value) => Number(value));
  const organizationsToAttach = organizations.filter((organization) => !previousOrganizationIds.includes(organization));
  const organizationsToDetach = previousOrganizationIds.filter((organization) => !organizations.includes(organization));
  if (organizationsToDetach.length) detachOrganization(organizationsToDetach);
  if (organizationsToAttach.length) attachOrganization(organizationsToAttach);
  organizationModalOpen.value = false;
};
</script>

<template>
  <Card>
    <template #header>
      <h2>Organizations view</h2>
      <Chip>{{ organizationCount }}</Chip>
      <div
        v-if="!authStore.currentInstanceIsMasterTenant && can([PERMISSIONS.CLINICIAN_ATTACH_ORGANIZATIONS])"
        class="ml-auto"
      >
        <AppButton
          type="text"
          size="small"
          :text="addEditOrgsButtonText"
          icon-left="add"
          @click="openAddOrganizations"
        />
      </div>
    </template>
    <template #content>
      <DataTable
        :value="organizations"
        :global-filter-fields="['name']"
        :scrollable="true"
        loading-icon="logo-spinner"
        :loading="loading"
        data-key="name"
        responsive-layout="scroll"
        scroll-height="440px"
        state-key="session-clinician-organization-store-table"
        state-storage="session"
        :class="{ 'table-loading': loading }"
      >
        <template v-if="organizationCount">
          <Column body-class="w-full" header-class="w-full" field="name" header="Organization">
            <template #body="{ data }">
              <OrganizationConnections :organizations="data" />
            </template>
          </Column>
          <Column
            body-class="justify-content-center mw-10rem"
            header-class="justify-content-center mw-10rem"
            field="id"
            header="Actions"
          >
            <template #body="{ data }">
              <ActionMenu v-if="createMenuModel(data).length" :menu-items="createMenuModel(data)" icon="more_horiz" />
              <span v-else>-</span>
            </template>
          </Column>
        </template>
        <template #empty>Organizations not assigned</template>
      </DataTable>
      <AddOrganizationModal
        v-if="organizationModalOpen && can([PERMISSIONS.CLINICIAN_ORGANIZATIONS_AVAILABLE_TO_ADD])"
        :title="addEditOrgsButtonText"
        :items="organizationItems"
        :searchable="true"
        :multi-selection="true"
        :selected-items-data="getUsersCurrentOrganizations"
        @cancel="onCancelAdd"
        @save="onSaveAdd"
      />
    </template>
  </Card>
</template>

<style lang="scss" scoped></style>
